import type { Interval } from 'date-fns'
import {
  addDays,
  addMinutes,
  areIntervalsOverlapping,
  compareAsc,
  differenceInMinutes,
  isBefore,
  parse,
} from 'date-fns'
import { format } from 'date-fns/format'

import type { TimeObject } from '@/modules/creator-settings/rebill-messages/types.ts'

export const parseTime = (time: string) => parse(time, 'HH:mm:ss', new Date())
export const getHoursAndMinutes = (time: Date) => {
  return {
    hour: parseInt(format(time, 'HH'), 10),
    minute: parseInt(format(time, 'mm'), 10),
    second: 0,
    nano: 0,
  }
}

export const parseTimeStringToTimObject = (timeString: string): TimeObject => {
  const time = timeString ? timeString : '00:00:00'
  const parsedTime = parse(time, 'HH:mm:ss', new Date())

  return {
    hour: parsedTime.getHours(),
    minute: parsedTime.getMinutes(),
    second: parsedTime.getSeconds(),
    nano: 0,
  }
}

export const getDateWithHoursAndMinutesFromTimeObject = (
  time?: TimeObject | null,
) => {
  const date = new Date()

  if (time?.hour !== undefined && !isNaN(time?.hour)) {
    date.setHours(time.hour)
  }

  if (time?.minute !== undefined && !isNaN(time?.minute)) {
    date.setMinutes(time.minute)
  }

  date.setSeconds(0)
  date.setMilliseconds(0)

  return date
}
/*
 * This function calls for data from backend, so here can be anything that will be return from backend
 * */
export const compareTimes = (time1: string, time2: string): number => {
  if (
    !time1 ||
    !time2 ||
    typeof time1 !== 'string' ||
    typeof time2 !== 'string'
  ) {
    return 0
  }
  const parsedTime1 = parse(time1, 'HH:mm:ss', new Date())
  const parsedTime2 = parse(time2, 'HH:mm:ss', new Date())

  return compareAsc(parsedTime1, parsedTime2)
}

export const displayTime = (time: Date) => {
  return new Intl.DateTimeFormat('default', {
    hour12: true,
    hour: 'numeric',
    minute: 'numeric',
  }).format(time)
}

export function normalizeInterval({ end, start }: Interval): Interval {
  if (isBefore(end, start)) {
    end = addDays(end, 1)
  }

  return { end, start }
}

export const getOverlappingIntervalsIndexes = (
  start: Date,
  end: Date,
  bookedIntervals: { start: string; end: string }[],
) => {
  const selectedTimeFrame = normalizeInterval({
    start: getDateWithHoursAndMinutesFromTimeObject(getHoursAndMinutes(start)),
    end: getDateWithHoursAndMinutesFromTimeObject(getHoursAndMinutes(end)),
  })

  return bookedIntervals.reduce<number[]>((acc, { start, end }, index) => {
    const existingTimeFrame = normalizeInterval({
      start: parseTime(start),
      end: parseTime(end),
    })

    if (
      areIntervalsOverlapping(selectedTimeFrame, existingTimeFrame, {
        inclusive: true,
      })
    ) {
      acc.push(index)
    }

    return acc
  }, [])
}

export const getLongestFreeInterval = (
  bookedIntervals?: Array<{ start: string; end: string }>,
) => {
  if (!bookedIntervals?.length) {
    return {
      start: '00:00:00',
      end: '23:59:00',
      duration: 1439,
    }
  }

  const freeIntervals = bookedIntervals.map((curr, i, arr) => {
    const freeIntervalStart = addMinutes(parseTime(curr.end), 1)
    const isOverDay = !arr[i + 1]
    const bookedIntervalStart = parseTime(arr[isOverDay ? 0 : i + 1].start)

    if (
      freeIntervalStart.toTimeString() === bookedIntervalStart.toTimeString()
    ) {
      return {
        start: freeIntervalStart,
        end: freeIntervalStart,
        duration: 0,
      }
    }
    const freeIntervalEnd = addMinutes(
      bookedIntervalStart,
      isOverDay ? 1439 : -1,
    )
    const duration = Math.abs(
      differenceInMinutes(freeIntervalStart, freeIntervalEnd),
    )

    return {
      start: freeIntervalStart,
      end: freeIntervalEnd,
      duration,
    }
  })

  const longestInterval = freeIntervals.reduce((longest, current) =>
    current.duration > longest.duration ? current : longest,
  )

  return {
    start: format(longestInterval.start, 'HH:mm:ss'),
    end: format(longestInterval.end, 'HH:mm:ss'),
    duration: longestInterval.duration,
  }
}
